import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ResponseError, responseErrorsResolver } from '@ui/src';
import { NewCofProps, AccessRight, SignatureType } from '../../../common/types';
import {
  useAddCorporateCOFMutation,
  useLazyGetCorporateCOFQuery,
} from '../../store/services/corporate';
import useGetAutocompleteList from '../../../new-application/helpers/use-autocomplete-list';
import { NatureOfControlEnum } from '../../../new-application/types';
import { CofProps } from '../../types';

const useSelectCompanyOfficial = ({ customerId }: { customerId?: string }) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [formValues, setFormValues] = useState<NewCofProps>();
  const [percentageError, setPercentageError] = useState('');
  const [cofError, setCofError] = useState('');

  const navigate = useNavigate();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const [addNewCof, { isLoading, isSuccess }] = useAddCorporateCOFMutation();
  const [
    getCorporateCof,
    { isLoading: cofIsLoading, isSuccess: cofLoadingIsSuccess },
  ] = useLazyGetCorporateCOFQuery();

  const {
    autocompleteList,
    onSearch,
    isLoading: autocompleteIsLoading,
    searchQuery,
    setSearchQuery,
    getAutocompleteListAction,
  } = useGetAutocompleteList();

  const addNewCofAction = useCallback(async () => {
    setCofError('');

    const body: {
      userId: string;
      email: string;
      firstName: string;
      lastName: string;
      phone: string;
      organizationId: string;
      position: string;
      companyOfficialType: string;
      signatureType: SignatureType;
      dob?: string;
      percentage?: string;
      natureOfControl?: NatureOfControlEnum;
      accessRight: AccessRight;
    } = {
      userId: formValues?.userId as string,
      email: formValues?.email as string,
      firstName: formValues?.firstName as string,
      lastName: formValues?.lastName as string,
      phone: formValues?.phone as string,
      organizationId: customerId as string,
      position: formValues?.position as string,
      companyOfficialType:
        formValues?.companyOfficialType || 'COMPANY_OFFICIAL',
      signatureType: formValues?.signatureType as SignatureType,
      accessRight:
        (formValues?.accessRight as AccessRight) || AccessRight.VIEWER,
    };

    if (formValues?.dob) {
      const dob = new Date(formValues.dob);

      body.dob = `${dob.getFullYear()}-${(dob.getMonth() + 1)
        .toString()
        .padStart(2, '0')}-${dob.getDate().toString().padStart(2, '0')}`;
    }

    if (formValues?.havingControl) {
      body.percentage = formValues.percentage;
      body.natureOfControl = formValues.natureOfControl;
    }

    const response = (await addNewCof(body)) as {
      data?: { data: CofProps };
      error?: ResponseError | { data: { message: string } };
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error as ResponseError,
        setError,
        setGlobalError: setCofError,
      });
    }
  }, [formValues, customerId]);

  const handleClose = () => {
    setPanelIsOpen(false);

    setTimeout(() => {
      navigate(-1);
    }, 100);
  };

  useEffect(() => {
    if (isSuccess && customerId) {
      getCorporateCof(customerId);
    }
  }, [isSuccess, customerId]);

  useEffect(() => {
    if (cofLoadingIsSuccess) {
      handleClose();
    }
  }, [cofLoadingIsSuccess]);

  useEffect(() => {
    if (!panelIsOpen) {
      setPanelIsOpen(true);
    }
  }, [panelIsOpen]);

  useEffect(() => {
    if (
      formValues &&
      formValues.percentage &&
      !/^\d*\.?\d*$/.test(formValues.percentage)
    ) {
      setPercentageError('Percentage of control field must be numeric');
    } else {
      setPercentageError('');
    }
  }, [formValues]);

  return {
    selectedCustomerId,
    autocompleteList,
    panelIsOpen,
    handleClose,
    isLoading,
    cofIsLoading,
    addNewCofAction,
    formValues,
    onSearch,
    autocompleteIsLoading,
    cofError,
    setSelectedCustomerId,
    setFormValues,
    searchQuery,
    setSearchQuery,
    getAutocompleteListAction,
    percentageError,
    errors,
  };
};

export default useSelectCompanyOfficial;
